import type {
  Brand,
  ChallengeFilters,
  ChallengeLabel,
  ChallengeStatus,
  ChallengeWithBrand,
  PaginatedResponse,
} from '@/types'
import { apiClient } from './client'
import { getApiErrorMessage } from './errors'
import { t } from '@/i18n'

function centsToEur(cents: number | null | undefined): number {
  return typeof cents === 'number' ? cents / 100 : 0
}

interface BackendBrand {
  id: string
  name: string
  category: string | null
  isActive: boolean
}

interface BackendChallengeList {
  id: string
  brandId: string
  brand: {
    id: string
    name: string
    category: string | null
  }
  title: string
  description: string | null
  type: string | null
  heroImageUrl: string | null
  labels?: Array<{
    id: string
    code: string
    displayText: string
  }>
  startsAt: string | null
  endsAt: string | null
  isActive: boolean
  // Derived server-side from the admin flag and the schedule dates.
  // Optional: backends older than the derived status do not send it.
  status?: ChallengeStatus
  configuredCashbackAmountCents?: number | null
}

interface BackendPaginatedChallenges {
  data: BackendChallengeList[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface BackendChallengeDetail extends BackendChallengeList {
  termsUrl: string | null
  termsText: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

function mapLabels(labels: BackendChallengeList['labels']): ChallengeLabel[] {
  if (!Array.isArray(labels)) return []
  return labels
    .filter((label): label is NonNullable<BackendChallengeList['labels']>[number] => !!label)
    .map(label => ({
      id: label.id,
      code: label.code,
      displayText: label.displayText,
    }))
}

// Built per call so the fallback label matches the active language.
function challengePlaceholder(): string {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="400" viewBox="0 0 800 400">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#003DA5"/>
        <stop offset="1" stop-color="#1a5fcf"/>
      </linearGradient>
    </defs>
    <rect width="800" height="400" fill="url(#g)"/>
    <text x="400" y="210" text-anchor="middle" font-family="Arial, sans-serif" font-size="32" fill="white" opacity="0.9">
      P&amp;G Health
    </text>
    <text x="400" y="250" text-anchor="middle" font-family="Arial, sans-serif" font-size="18" fill="white" opacity="0.8">
      ${t('challenges.placeholderLabel')}
    </text>
  </svg>`
  )}`
}

function toBrand(brand: BackendBrand): Brand {
  return {
    id: brand.id,
    name: brand.name,
    description: brand.category ?? undefined,
  }
}

function toChallengeWithBrand(challenge: BackendChallengeList): ChallengeWithBrand {
  return {
    id: challenge.id,
    title: challenge.title,
    description: challenge.description ?? '',
    longDescription: undefined,
    imageUrl: challenge.heroImageUrl ?? challengePlaceholder(),
    rewardAmount: centsToEur(challenge.configuredCashbackAmountCents),
    // Fallback covers backends older than the derived status (e.g. after a backend rollback)
    status: challenge.status ?? (challenge.isActive ? 'active' : 'finalized'),
    brandId: challenge.brandId,
    startDate: challenge.startsAt ? new Date(challenge.startsAt) : new Date(),
    endDate: challenge.endsAt ? new Date(challenge.endsAt) : new Date(),
    termsUrl: undefined,
    maxParticipations: undefined,
    labels: mapLabels(challenge.labels),
    createdAt: new Date(0),
    brand: {
      id: challenge.brand.id,
      name: challenge.brand.name,
      description: challenge.brand.category ?? undefined,
    },
  }
}

function applyChallengeDetail(detail: BackendChallengeDetail): ChallengeWithBrand {
  const base = toChallengeWithBrand(detail)
  return {
    ...base,
    termsUrl: detail.termsUrl ?? undefined,
    longDescription: detail.termsText ?? undefined,
    createdAt: new Date(detail.createdAt),
  }
}

export const challengeService = {
  async getChallenges(
    filters?: ChallengeFilters,
    page: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedResponse<ChallengeWithBrand>> {
    try {
      const response = await apiClient.get<BackendPaginatedChallenges>('/api/v1/challenges', {
        params: {
          status: filters?.status ?? 'all',
          brandId: filters?.brandId ?? undefined,
          search: filters?.searchQuery || undefined,
          page,
          pageSize,
        },
      })

      return {
        data: response.data.data.map(toChallengeWithBrand),
        total: response.data.total,
        page: response.data.page,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
      }
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadChallenges')))
    }
  },

  async getChallenge(id: string): Promise<ChallengeWithBrand | null> {
    try {
      const response = await apiClient.get<BackendChallengeDetail>(`/api/v1/challenges/${id}`)
      return applyChallengeDetail(response.data)
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadChallenge')))
    }
  },

  async getActiveChallenges(limit?: number): Promise<ChallengeWithBrand[]> {
    const response = await this.getChallenges({ status: 'active', brandId: null, searchQuery: '' }, 1, limit ?? 20)
    return response.data
  },

  async getBrands(): Promise<Brand[]> {
    try {
      const response = await apiClient.get<BackendBrand[]>('/api/v1/brands')
      return response.data.map(toBrand)
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadBrands')))
    }
  },

  async getActiveBrands(): Promise<Brand[]> {
    return this.getBrands()
  },

  async getChallengesByBrand(brandId: string): Promise<ChallengeWithBrand[]> {
    const response = await this.getChallenges({ status: 'all', brandId, searchQuery: '' }, 1, 100)
    return response.data
  },

  async getFeaturedChallenges(count: number = 4): Promise<ChallengeWithBrand[]> {
    const response = await this.getChallenges({ status: 'active', brandId: null, searchQuery: '' }, 1, count)
    return response.data
  },
}
